import { useMemo, useState } from "react";

import { CameraIcon, InformationCircleIcon } from "@heroicons/react/24/outline";

import usePartMutations from "../../../data/usePartMutations";
import useDefaultCRUDHandlers from "../../../hooks/useDefaultCRUDHandlers";
import Modal from "../../../layouts/Modal";
import { IPart, IPartImageMpnAnalysis, IPartImageType } from "../../../models/Part";
import classNames from "../../../utils/classNames";
import ClickToCopy from "../../shared/ClickToCopy";
import EbayLinkButton from "../../shared/Inputs/EbayLinkButton";
import LoadingPulse from "../../shared/LoadingPulse";


export default function UnlistedNpmUpdate({ part, onTable = true }: { part: IPart, onTable?: boolean }) {
  const { patchPart, getMpnImageAnalysis } = usePartMutations();
  const { saveHandlers } = useDefaultCRUDHandlers("Part Number");

  const [open, setOpen] = useState(false);
  const [analysisOpen, setAnalysisOpen] = useState(false);
  const [analysisData, setAnalysisData] = useState<IPartImageMpnAnalysis | null>(null);

  const handleMpnUpdate = (mpn: string) => {
    const patchBody = {
      id: part.id,
      partNumber: mpn,
    };

    if (part.partNumber === mpn) return;
    if (!mpn) return;

    patch(patchBody);
  }

  const handleSupersededUpdate = (superseded: string) => {
    const patchBody = {
      id: part.id,
      supersededPartNumbers: superseded,
    };

    if (part.partNumber === superseded) return;
    if (!superseded) return;

    patch(patchBody);
  }

  const patch = (patchBody: any) => {
    patchPart.mutate(patchBody, {
      ...saveHandlers,
      onSuccess: () => {
        if (patchBody.partNumber) {
          part.partNumber = patchBody.partNumber;
        } else if (patchBody.supersededPartNumbers) {
          part.supersededPartNumbers = patchBody.supersededPartNumbers;
        }
        saveHandlers.onSuccess();
      },
    });
  }

  const mpnImage = useMemo(
    () => part.images?.find((image) => image.imageType === IPartImageType.MPN),
    [part.images]
  );

  const candidateMpns = useMemo(
    () =>
      analysisData?.candidateMpns
        ?.split(",")
        .map((x) => x.trim())
        .filter((x) => x.length > 0) ?? [],
    [analysisData?.candidateMpns]
  );

  const handleOpenAnalysis = (analysisId?: string | null) => {
    if (!analysisId) return;

    setAnalysisData(null);
    getMpnImageAnalysis.mutate(analysisId, {
      onSuccess: (response) => {
        setAnalysisData(response.data);
        setAnalysisOpen(true);
      },
      onError: () => {
        saveHandlers.onError();
      },
    });
  };

  if (patchPart.isLoading) {
    return <LoadingPulse />;
  }

  return (
    <>
      {/* Full-size modal */}
      <Modal open={open} setOpen={setOpen} width="max-w-7xl">
        <div>
          {mpnImage ? (
            <img className="w-full object-contain" src={mpnImage.url} />
          ) : (
            <CameraIcon className="w-16 text-gray-400 mx-auto" />
          )}
          <div className="p-1 mt-2">
            <label className="text-gray-700">MPN</label>
            <input
              key={part.id}
              defaultValue={part.partNumber ?? ""}
              className={classNames(
                onTable ? "w-full text-xs h-6 min-w-14" : "w-1/2",
                "border"
              )}
              type="text"
              onBlur={(e) => handleMpnUpdate(e.target.value)}
            />
          </div>

          <div className="p-1 mt-2">
            <label className="text-gray-700">Superseded Part Numbers</label>
            <input
              key={part.id}
              defaultValue={part.supersededPartNumbers ?? ""}
              className={classNames(
                onTable ? "w-full text-xs h-6 min-w-14" : "w-1/2",
                "border"
              )}
              type="text"
              onBlur={(e) => handleSupersededUpdate(e.target.value)}
            />
          </div>
        </div>
      </Modal>

      <Modal open={analysisOpen} setOpen={setAnalysisOpen} width="max-w-2xl">
        <div className="p-2 space-y-3">
          <h3 className="font-semibold text-lg">MPN Image Analysis</h3>

          {getMpnImageAnalysis.isLoading && <LoadingPulse />}

          {!getMpnImageAnalysis.isLoading && analysisData && (
            <>
              <div>
                <p className="text-xs text-gray-500">Found MPN</p>
                <p className="font-medium">{analysisData.foundMpn ? "Yes" : "No"}</p>
              </div>

              <div>
                <p className="text-xs text-gray-500">Best Match</p>
                <p className="font-medium">{analysisData.bestMatchMpn || "-"}</p>
              </div>

              <div>
                <p className="text-xs text-gray-500">Confidence</p>
                <p className="font-medium">{analysisData.confidence}%</p>
              </div>

              <div>
                <p className="text-xs text-gray-500">Manufacturer</p>
                <p className="font-medium">{analysisData.manufacturer || "-"}</p>
              </div>

              <div>
                <p className="text-xs text-gray-500">Candidate MPNs</p>
                <p className="font-medium">
                  {candidateMpns.length > 0 ? candidateMpns.join(", ") : "-"}
                </p>
              </div>

              <div>
                <p className="text-xs text-gray-500">Details</p>
                <p className="whitespace-pre-wrap">{analysisData.details || "-"}</p>
              </div>
            </>
          )}
        </div>
      </Modal>

      {/* Small inline display */}
      <div className="h-full flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div
          className={classNames(
            "flex flex-col justify-center relative",
            onTable && "items-center"
          )}
        >
          <span>
            {mpnImage ? (
              <img
                className="w-24 h-20 object-contain cursor-pointer hover:scale-110 transition-all"
                onClick={() => setOpen(true)}
                src={mpnImage.thumbnailUrl ?? mpnImage.url}
              />
            ) : (
              <div className="flex items-center gap-x-2">
                {!part.partNumber && (
                  <p className="text-xs text-gray-500">No MPN</p>
                )}
                <CameraIcon className="w-8 text-gray-600" />
              </div>
            )}
          </span>



          <div className="w-full">
            <label className="text-gray-700 text-xs">MPN</label>
            <input
              key={part.id}
              defaultValue={part.partNumber ?? ""}
              className={classNames(
                onTable ? "w-full text-xs h-6" : "w-1/2",
                "border p-1"
              )}
              type="text"
              onBlur={(e) => handleMpnUpdate(e.target.value)}
            />

            <div className="flex items-center gap-1 mt-1">
              <ClickToCopy value={part.partNumber} text={undefined} />

              {part.partNumber && (
                <EbayLinkButton query={part.partNumber} />
              )}

              {mpnImage?.partImageMpnAnalysisId && (
                <button
                  type="button"
                  title="View MPN analysis"
                  className="border rounded p-1 hover:bg-gray-50"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleOpenAnalysis(mpnImage.partImageMpnAnalysisId);
                  }}
                >
                  <InformationCircleIcon className="w-5 h-5 text-gray-700" />
                </button>
              )}
            </div>
          </div>

          <div className="flex gap-1 mt-1 w-full">
            <div className="w-full">
              <label className="text-gray-700 text-xs">Superseded Part Numbers</label>
              <input
                key={part.id}
                defaultValue={part.supersededPartNumbers ?? ""}
                className={classNames(
                  onTable ? "w-full text-xs h-6" : "w-1/2",
                  "border p-1"
                )}
                type="text"
                onBlur={(e) => handleSupersededUpdate(e.target.value)}
              />
            </div>
            <ClickToCopy value={part.supersededPartNumbers} text={undefined} className="mt-4" />
          </div>

          {part.oePartNumber && part.oePartNumber !== part.partNumber && (
            <>
              <div className="text-gray-700 mt-2 text-center">
                <p>OE PN:</p>
                <ClickToCopy text={part.oePartNumber} />
              </div>
              <EbayLinkButton query={part.oePartNumber} />
            </>
          )}
        </div>
      </div>
    </>
  );
}